import { useForm } from "@tanstack/react-form";
import { useQueryClient, useSuspenseQueries } from "@tanstack/react-query";
import {
  CatchBoundary,
  createFileRoute,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { type ReactNode, Suspense, useState } from "react";
import { ApiError } from "@/api/errors";
import { personaPresetsQuery, personaQuery, savePersona } from "@/api/persona";
import { finishOnboarding } from "@/auth/onboarding";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldTitle,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { WhatsappConnection } from "@/components/whatsapp-connection";
import { personaSchema } from "@/lib/schemas";

/**
 * Primeira tela depois do cadastro: a conta já existe e a sessão já está de
 * pé, mas o painel ainda não faz sentido nenhum — o que decide se o bot vai
 * soar como o negócio é isto aqui. Por isso fica fora do `_authed`: sem
 * sidebar e sem breadcrumb, uma decisão de cada vez.
 *
 * São dois passos, nesta ordem de propósito: a persona é leve e agradável
 * de decidir, a conexão do WhatsApp é burocracia da Meta que pode falhar
 * ou depender de coisas que a pessoa não tem à mão agora. Persona primeiro
 * garante que ela fique salva mesmo se a conexão for abandonada no meio.
 */
export const Route = createFileRoute("/bem-vindo")({
  beforeLoad: ({ context, location }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
  },
  component: WelcomePage,
  // A conta acabou de nascer: uma tela branca logo depois do cadastro é o
  // pior desfecho possível. Se a persona não carregar, esta tela pelo menos
  // conta o que houve e não prende ninguém aqui.
  errorComponent: () => <PersonaUnavailable />,
});

function WelcomeShell({
  title,
  step,
  description,
  children,
}: {
  title: string;
  step?: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-muted p-6 md:p-10">
      <div className="w-full max-w-xl">
        <Card>
          <CardHeader>
            {/* Saber que são dois passos muda a disposição de encarar o
                segundo — sem isso, a conexão do WhatsApp aparece como uma
                surpresa depois de algo que parecia ser o fim. */}
            {step && (
              <p className="text-xs font-medium text-muted-foreground">{step}</p>
            )}
            <CardTitle className="text-2xl">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </CardHeader>
          <CardContent>{children}</CardContent>
        </Card>
      </div>
    </div>
  );
}

type Step = "persona" | "whatsapp";

function WelcomePage() {
  const [step, setStep] = useState<Step>("persona");
  const [resetKey, setResetKey] = useState(0);

  if (step === "whatsapp") return <WhatsappStep />;

  return (
    // A persona falhar não pode custar a conexão do WhatsApp, que é de onde
    // o produto tira o valor inteiro. Este boundary local mantém o passo 2
    // alcançável; o `errorComponent` da rota segue valendo pro que quebrar
    // fora daqui.
    <CatchBoundary
      getResetKey={() => resetKey}
      errorComponent={() => (
        <PersonaUnavailable
          onRetry={() => setResetKey((key) => key + 1)}
          onSkip={() => setStep("whatsapp")}
        />
      )}
    >
      <WelcomeShell
        title="Sua conta está pronta"
        step="Passo 1 de 2"
        description="Vamos começar pelo jeito que o assistente conversa com seus clientes. Dá pra mudar tudo isso depois, nas configurações."
      >
        <Suspense fallback={<PersonaSkeleton />}>
          <PersonaForm onSaved={() => setStep("whatsapp")} />
        </Suspense>
      </WelcomeShell>
    </CatchBoundary>
  );
}

/**
 * O segundo passo. Fica aqui o convite pra conectar, mas quem sabe fazer a
 * conexão é o componente compartilhado com as configurações — é lá que ela
 * precisa existir de qualquer jeito, pra retentar ativação e acompanhar a
 * aprovação dos templates dias depois.
 */
function WhatsappStep() {
  const router = useRouter();
  const [connected, setConnected] = useState(false);

  function goToPanel() {
    // Dispensar é uma decisão também: o painel não pode ficar devolvendo o
    // dono pra cá a cada navegação. Quem saiu daqui sem conectar continua
    // sendo alcançável pelo `GET /waba/readiness`, que é servidor e não
    // morre junto com a aba.
    finishOnboarding();
    void router.navigate({ to: "/" });
  }

  return (
    <WelcomeShell
      title={connected ? "Tudo pronto" : "Agora o WhatsApp"}
      step="Passo 2 de 2"
      description={
        connected
          ? "Seu assistente já pode atender seus clientes."
          : "É por ele que o assistente atende. Sem essa conexão o bot não recebe ninguém — mas dá pra fazer isso depois, nas configurações."
      }
    >
      <div className="flex flex-col gap-6">
        <WhatsappConnection onConnected={() => setConnected(true)} />
        <Button
          type="button"
          className="w-full"
          variant={connected ? "default" : "outline"}
          onClick={goToPanel}
        >
          {connected ? "Ir para o painel" : "Deixar pra depois"}
        </Button>
      </div>
    </WelcomeShell>
  );
}

/**
 * Sem `onRetry`/`onSkip` é o `errorComponent` da rota; com eles, o fallback
 * do boundary do formulário — que sabe voltar pro passo seguinte em vez de
 * mandar todo mundo pro painel.
 */
function PersonaUnavailable({
  onRetry,
  onSkip,
}: {
  onRetry?: () => void;
  onSkip?: () => void;
}) {
  const router = useRouter();
  const queryClient = useQueryClient();

  return (
    <WelcomeShell
      title="Sua conta está pronta"
      description="Sua conta foi criada, mas não conseguimos carregar as opções do assistente agora. Você pode tentar de novo ou configurar isso mais tarde, nas configurações."
    >
      <Field orientation="horizontal">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            // A query falhou e o erro ficou em cache: sem limpar, o retry
            // reencontra o mesmo erro na hora, sem nem tentar a rede.
            void queryClient.resetQueries({ queryKey: ["persona"] });
            if (onRetry) onRetry();
            else void router.invalidate();
          }}
        >
          Tentar de novo
        </Button>
        <Button
          type="button"
          className="flex-1"
          onClick={() => {
            if (onSkip) {
              onSkip();
              return;
            }
            finishOnboarding();
            void router.navigate({ to: "/" });
          }}
        >
          {onSkip ? "Continuar" : "Ir para o painel"}
        </Button>
      </Field>
    </WelcomeShell>
  );
}

function PersonaSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
}

function PersonaForm({ onSaved }: { onSaved: () => void }) {
  const [saveError, setSaveError] = useState<string | null>(null);

  // As duas juntas: o formulário abre com a persona que o bot já usa, e a
  // lista de estilos é o que este vertical tem pra oferecer.
  const [{ data: presets }, { data: persona }] = useSuspenseQueries({
    queries: [personaPresetsQuery(), personaQuery()],
  });

  const form = useForm({
    defaultValues: {
      // Sem preset escolhido, pré-seleciona o primeiro: quem chegou aqui pelo
      // cadastro veio pra decidir, e nenhuma opção marcada convida a passar
      // direto. Catálogo vazio deixa `""`, que o backend aceita.
      preset_id: persona.preset_id ?? presets[0]?.id ?? "",
      bot_name: persona.bot_name,
      professional_label: persona.professional_label,
      use_emoji: persona.use_emoji,
    },
    validators: { onSubmit: personaSchema },
    onSubmit: async ({ value }) => {
      setSaveError(null);
      try {
        await savePersona(value);
        // Segue pro passo 2 em vez de ir ao painel: a persona já está
        // gravada, então o que vier depois não a coloca em risco.
        onSaved();
      } catch (cause) {
        setSaveError(describePersonaError(cause));
      }
    },
  });

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void form.handleSubmit();
      }}
    >
      <FieldGroup className="gap-5">
        {presets.length > 0 && (
          <form.Field name="preset_id">
            {(field) => (
              <Field data-invalid={field.state.meta.errors.length > 0}>
                <FieldLabel>Estilo de conversa</FieldLabel>
                <RadioGroup
                  className="gap-2"
                  value={field.state.value}
                  onValueChange={field.handleChange}
                >
                  {presets.map((preset) => (
                    <FieldLabel
                      key={preset.id}
                      htmlFor={`preset-${preset.id}`}
                      className="[&>[data-slot=field]]:p-3"
                    >
                      <Field orientation="horizontal">
                        <FieldContent>
                          <FieldTitle>{preset.label}</FieldTitle>
                          {/* A descrição vem do preset. Antes saía do
                              `tone`, e os dois estilos informais do mesmo
                              vertical apareciam prometendo a mesma coisa.
                              Preset antigo pode vir sem ela — aí só o
                              rótulo, que já diz o essencial. */}
                          {preset.description && (
                            <FieldDescription>{preset.description}</FieldDescription>
                          )}
                        </FieldContent>
                        {/* Botão com role=radio não herda nome de <label for>. */}
                        <RadioGroupItem
                          id={`preset-${preset.id}`}
                          value={preset.id}
                          aria-label={preset.label}
                        />
                      </Field>
                    </FieldLabel>
                  ))}
                </RadioGroup>
                <FieldError errors={field.state.meta.errors} />
              </Field>
            )}
          </form.Field>
        )}

        {/* `GET /verticals` só oferece segmento que tem persona, então quem
            se cadastra hoje não cai aqui. Cai quem tem um estabelecimento
            cujo vertical saiu do catálogo — e aí o resto da tela continua
            valendo, não é motivo pra bloquear. */}
        {presets.length === 0 && (
          <FieldDescription>
            Os estilos de conversa ainda não estão disponíveis para o seu segmento. O
            assistente vai usar o tom padrão, e você pode escolher um estilo depois, nas
            configurações.
          </FieldDescription>
        )}

        <form.Field name="bot_name">
          {(field) => (
            <Field data-invalid={field.state.meta.errors.length > 0}>
              <FieldLabel htmlFor={field.name}>Nome do assistente</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                maxLength={40}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                aria-invalid={field.state.meta.errors.length > 0}
              />
              <FieldDescription>
                É como ele se apresenta na primeira mensagem.
              </FieldDescription>
              <FieldError errors={field.state.meta.errors} />
            </Field>
          )}
        </form.Field>

        <form.Field name="professional_label">
          {(field) => (
            <Field data-invalid={field.state.meta.errors.length > 0}>
              <FieldLabel htmlFor={field.name}>Como chamar quem atende</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                maxLength={30}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                aria-invalid={field.state.meta.errors.length > 0}
              />
              <FieldDescription>
                O bot usa esta palavra ao falar da sua equipe: "escolha o{" "}
                {field.state.value.trim() || "profissional"}".
              </FieldDescription>
              <FieldError errors={field.state.meta.errors} />
            </Field>
          )}
        </form.Field>

        <form.Field name="use_emoji">
          {(field) => (
            <Field orientation="horizontal">
              <FieldContent>
                <FieldLabel htmlFor={field.name}>Usar emoji</FieldLabel>
                <FieldDescription>
                  Deixa a conversa mais leve. Combina menos com tom formal.
                </FieldDescription>
              </FieldContent>
              <Switch
                id={field.name}
                checked={field.state.value}
                onCheckedChange={field.handleChange}
              />
            </Field>
          )}
        </form.Field>

        {saveError && (
          <p role="alert" className="text-sm text-destructive">
            {saveError}
          </p>
        )}

        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Field orientation="horizontal">
              <Button
                type="button"
                variant="outline"
                disabled={isSubmitting}
                onClick={onSaved}
              >
                {/* Pula a persona, não o onboarding: o WhatsApp vem a
                    seguir e é o que o produto realmente depende — quem
                    quiser sair de tudo sai de lá, num clique. */}
                Pular esta etapa
              </Button>
              <Button type="submit" className="flex-1" disabled={isSubmitting}>
                {isSubmitting ? "Salvando…" : "Salvar e continuar"}
              </Button>
            </Field>
          )}
        </form.Subscribe>
      </FieldGroup>
    </form>
  );
}

function describePersonaError(cause: unknown): string {
  if (!(cause instanceof ApiError)) return "Não foi possível salvar agora.";
  switch (cause.code) {
    case "persona_preset_vertical_mismatch":
      return "Esse estilo não é do segmento do seu negócio.";
    case "persona_preset_not_found":
      return "Esse estilo não existe mais. Escolha outro.";
    default:
      return "Não foi possível salvar agora. Tente de novo.";
  }
}
